import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import SuperAdminDashboard from './SuperAdminDashboard';
import AngularAppEmbed from './components/AngularAppEmbed';
import BrandBand from './components/BrandBand';
import { description, steps, support_contact } from './siteContent';
import './App.css';

// Configure axios defaults
axios.defaults.baseURL = '/api';
axios.defaults.withCredentials = true;

// Set up request interceptor at module level so token is always sent
axios.interceptors.request.use(
  (config) => {
    const currentToken = localStorage.getItem('auth_token');
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [showAngularApp, setShowAngularApp] = React.useState(false);
  const [currentGroupId, setCurrentGroupId] = React.useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Set up response interceptor for token expiration (needs navigate)
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('auth_token');
          setToken(null);
          setUser(null);
          navigate('/');
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, [navigate]);

  useEffect(() => {
    // Handle auth success callback with JWT token
    if (location.pathname === '/auth-success') {
      handleAuthSuccess();
    }
  }, [location]);

  // Separate useEffect for checking auth status on component mount
  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token');
    if (storedToken && !user) {
      setToken(storedToken);
      checkAuthStatus().catch(console.error);
    }
  }, []); // Only run on mount

  const handleAuthSuccess = async () => {
    setLoading(true);
    setError('');
    
    try {
      const urlParams = new URLSearchParams(location.search);
      const jwtToken = urlParams.get('token');
      
      if (jwtToken) {
        // Store the JWT token
        localStorage.setItem('auth_token', jwtToken);
        setToken(jwtToken);
        
        // Get user info using the token
        await checkAuthStatus();
        
        // Redirect to main page
        navigate('/');
      } else {
        throw new Error('No token received');
      }
    } catch (error) {
      console.error('Auth success error:', error);
      setError('Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const checkAuthStatus = async () => {
    try {
      // Get live user profile data from GroupMe
      const response = await axios.get('/v1/user/profile');
      if (response.data.user) {
        setUser(response.data.user);
        // Check super admin status after setting user
        await checkSuperAdminStatus();
        return true;
      }
      return false;
    } catch (error) {
      if (error.response?.status === 401) {
        console.log('Not authenticated or token expired');
        localStorage.removeItem('auth_token');
        setToken(null);
        setUser(null);
        setIsSuperAdmin(false);
      } else if (error.response?.status === 429) {
        console.log('GroupMe API rate limited, will retry...');
        setError('GroupMe API is rate limited. Please wait a moment and refresh the page.');
        // Don't clear auth tokens for rate limiting - user is still authenticated
        return false;
      } else {
        console.error('Auth check error:', error);
      }
      return false;
    }
  };

  const checkSuperAdminStatus = async () => {
    try {
      const response = await axios.get('/v1/admin/all-groups');
      setIsSuperAdmin(true);
    } catch (error) {
      if (error.response?.status === 403) {
        setIsSuperAdmin(false);
      } else {
        console.error('Super admin check error:', error);
        setIsSuperAdmin(false);
      }
    }
  };

  const handleLogin = () => {
    // Redirect to backend OAuth initiation endpoint
    // With nginx routing, both dev and prod use the same relative URL
    window.location.href = '/api/v1/auth/login';
  };

  const handleLogout = async () => {
    try {
      await axios.post('/api/v1/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage and state regardless of API call success
      localStorage.removeItem('auth_token');
      setToken(null);
      setUser(null);
      setIsSuperAdmin(false);
      navigate('/');
    }
  };

  // Example: Show Angular app when user navigates to a group
  const handleGroupAccess = (groupId) => {
    setCurrentGroupId(groupId);
    setShowAngularApp(true);
  };

  if (showAngularApp && currentGroupId) {
    return (
      <AngularAppEmbed 
        groupId={currentGroupId}
        authToken={localStorage.getItem('authToken')}
      />
    );
  }

  if (loading) {
    return (
      <LoadingStatus />
    );
  }

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={
          user ? (
            <Dashboard user={user} isSuperAdmin={isSuperAdmin} onLogout={handleLogout} onGroupAccess={handleGroupAccess} />
          ) : (
            <Home onLogin={handleLogin} error={error} />
          )
        } />
        <Route path="/super-admin" element={
          user ? (
            isSuperAdmin ? (
              <SuperAdminDashboard user={user} onLogout={handleLogout} />
            ) : (
              <>
                <BrandBand userName={user.name} onLogout={handleLogout} />
                <div className="ui-page">
                  <div className="ui-card ui-prose">
                    <h2>Access Denied</h2>
                    <p>You don't have permission to access the admin panel.</p>
                    <button onClick={() => navigate('/')} className="ui-button ui-button--primary">
                      Return to Dashboard
                    </button>
                  </div>
                </div>
              </>
            )
          ) : (
            <Home onLogin={handleLogin} error={error} />
          )
        } />
        <Route path="/auth-success" element={<LoadingStatus />} />
      </Routes>
    </div>
  );
}

/** Spinner with a text label, announced to assistive tech via role="status". */
function LoadingStatus() {
  return (
    <>
      <BrandBand />
      <div className="ui-page">
        <div className="ui-empty" role="status">
          <div className="ui-spinner" aria-hidden="true"></div>
          <p>Signing you in...</p>
        </div>
      </div>
    </>
  );
}

/**
 * One paragraph "<lead> <name> - <email> - <phone>" built from support_contact,
 * with mailto and tel links. Renders only the fields that are set and nothing
 * at all when none are.
 */
function ContactLine({ lead }) {
  const { name, email, phone } = support_contact;
  const contact = [
    name && <span key="name">{name}</span>,
    email && (
      <a key="email" href={`mailto:${email}`}>
        {email}
      </a>
    ),
    phone && (
      <a key="phone" className="ui-nowrap" href={`tel:${phone.replace(/[\s-]/g, '')}`}>
        {phone}
      </a>
    ),
  ].filter(Boolean);

  if (contact.length === 0) return null;
  return (
    <p>
      {lead}{' '}
      {contact.flatMap((part, i) => (i === 0 ? [part] : [' - ', part]))}
    </p>
  );
}

function Home({ onLogin, error }) {
  return (
    <>
      <BrandBand />
      <div className="ui-page">
        <div className="ui-card ui-prose">
          <h1>YaBuddy Registration</h1>
          <p>{description}</p>

          <h2>How it works</h2>
          <ol>
            {steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>

          {error && (
            <div className="ui-alert ui-alert--error" role="alert">
              {error}
            </div>
          )}

          <button onClick={onLogin} className="ui-button ui-button--primary">
            Login with GroupMe
          </button>

          <ContactLine lead="Questions? Contact" />
        </div>
      </div>
    </>
  );
}

/**
 * Plain-language message for a failed Add. The backend answers a missing or
 * placeholder BOT_CALLBACK_URL with HTTP 500 {error: 'Bot callback URL is not
 * configured...'}, and a GroupMe callback rejection with HTTP 400 {error:
 * 'Bot callback URL validation failed...', details}. Neither has an error
 * code, so both are recognised by "callback url" in the error text.
 */
function describeAddError(error) {
  const apiError = error.response?.data?.error || '';
  if (/callback url/i.test(apiError)) {
    const { name, email, phone } = support_contact;
    const contact = [name, email, phone].filter(Boolean).join(' - ');
    return `YaBuddy isn't fully set up yet, so it can't be added to a group. Please contact ${contact || 'the site administrator'}.`;
  }
  if (/already exists/i.test(apiError)) {
    return 'YaBuddy is already in this group. Refresh the page to see its current status.';
  }
  return "We couldn't add YaBuddy to this group. Please try again in a moment, or contact support if it keeps happening.";
}

const SEARCH_MIN_GROUPS = 9;

// Orders groups once: bot groups first, API order otherwise (R-006). Later
// calls pass the IDs already on screen so cards keep their positions and
// unseen groups are appended in API order.
function orderGroups(fetched, shownIds) {
  if (!shownIds) {
    return [
      ...fetched.filter((group) => group.has_yabuddy_bot),
      ...fetched.filter((group) => !group.has_yabuddy_bot),
    ];
  }
  const position = new Map(shownIds.map((id, index) => [id, index]));
  const known = fetched
    .filter((group) => position.has(group.id))
    .sort((a, b) => position.get(a.id) - position.get(b.id));
  return [...known, ...fetched.filter((group) => !position.has(group.id))];
}

function Dashboard({ user, isSuperAdmin, onLogout, onGroupAccess }) {
  const [groups, setGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const shownIds = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingGroups, setProcessingGroups] = useState(new Set());
  const [currentUser, setCurrentUser] = useState(user);
  const [copyNotification, setCopyNotification] = useState('');
  const [addedGroupId, setAddedGroupId] = useState(null);
  const [confirmingGroupId, setConfirmingGroupId] = useState(null);
  const confirmButton = useRef(null);

  // Move focus to "Confirm remove" when a card opens its confirm (R-009).
  useEffect(() => {
    if (confirmingGroupId !== null) confirmButton.current?.focus();
  }, [confirmingGroupId]);

  useEffect(() => {
    if (user) {
      setCurrentUser(user);
      fetchGroups();
      // Refresh user data every 30 seconds to get live avatar/profile updates
      const interval = setInterval(refreshUserData, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const refreshUserData = async () => {
    try {
      const response = await axios.get('/v1/user/profile');
      if (response.data.user) {
        setCurrentUser(response.data.user);
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      // Only the first load replaces the page with the spinner; a refetch
      // updates the cards in place.
      if (shownIds.current === null) setLoading(true);
      const response = await axios.get('/v1/user/groups');
      const ordered = orderGroups(response.data.groups || [], shownIds.current);
      if (ordered.length > 0) {
        shownIds.current = ordered.map((group) => group.id);
      }
      setGroups(ordered);
      setError('');
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      setError('Failed to load your GroupMe groups. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  const addBot = async (groupId) => {
    setAddedGroupId(null);
    setProcessingGroups(prev => new Set(prev).add(groupId));
    try {
      const response = await axios.post('/v1/bot/add', {
        group_id: groupId
      });
      
      if (response.data.success) {
        setAddedGroupId(groupId);
        // Refresh groups to show updated bot status
        await fetchGroups();
      }
    } catch (error) {
      console.error('Failed to add bot:', error);
      setError(describeAddError(error));
    } finally {
      setProcessingGroups(prev => {
        const newSet = new Set(prev);
        newSet.delete(groupId);
        return newSet;
      });
    }
  };

  const removeBot = async (botId, groupId) => {
    setAddedGroupId(null);
    setProcessingGroups(prev => new Set(prev).add(groupId));
    try {
      const response = await axios.post('/v1/bot/remove', {
        bot_id: botId
      });
      
      if (response.data.success) {
        // Refresh groups to show updated bot status
        await fetchGroups();
      }
    } catch (error) {
      console.error('Failed to remove bot:', error);
      setError(error.response?.data?.error || 'Failed to remove bot from group');
    } finally {
      setProcessingGroups(prev => {
        const newSet = new Set(prev);
        newSet.delete(groupId);
        return newSet;
      });
    }
  };

  const confirmRemove = async (botId, groupId) => {
    await removeBot(botId, groupId);
    // Close only this card's confirm; another card may have opened meanwhile.
    setConfirmingGroupId((current) => (current === groupId ? null : current));
  };

  const needle = searchTerm.trim().toLowerCase();
  const visibleGroups = needle
    ? groups.filter((group) => (group.name || '').toLowerCase().includes(needle))
    : groups;

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyNotification('Bot ID copied to clipboard!');
      setTimeout(() => setCopyNotification(''), 3000);
      console.log('Bot ID copied to clipboard:', text);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopyNotification('Bot ID copied to clipboard!');
      setTimeout(() => setCopyNotification(''), 3000);
      console.log('Bot ID copied to clipboard (fallback):', text);
    }
  };

  const defaultAvatar = 'data:image/svg+xml;charset=UTF-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"%3E%3Ccircle cx="20" cy="20" r="20" fill="%23e5e7eb"/%3E%3Cpath d="M20 8c3.3 0 6 2.7 6 6s-2.7 6-6 6-6-2.7-6-6 2.7-6 6-6zm0 10c4.4 0 8 2.2 8 5v7H12v-7c0-2.8 3.6-5 8-5z" fill="%23374151"/%3E%3C/svg%3E';
  const defaultGroup = 'data:image/svg+xml;charset=UTF-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"%3E%3Crect width="40" height="40" rx="4" fill="%23374151"/%3E%3Cpath d="M14 16c0-1.1.9-2 2-2h8c1.1 0 2 .9 2 2v8c0 1.1-.9 2-2 2h-8c-1.1 0-2-.9-2-2v-8z" stroke="%23fff" stroke-width="2" fill="none"/%3E%3C/svg%3E';

  if (!currentUser) {
    return (
      <>
        <BrandBand />
        <div className="ui-page">
          <h2>Please log in to access the dashboard</h2>
          <a href="/">← Back to Home</a>
        </div>
      </>
    );
  }

  return (
    <>
      <BrandBand userName={currentUser.name} onLogout={onLogout} />
      <div className="ui-page">
        {copyNotification && (
          <div className="ui-toast">
            {copyNotification}
          </div>
        )}
        <div className="ui-card ui-header">
          <div className="ui-cluster ui-header__main">
            <img
              src={currentUser.image_url || currentUser.avatar_url || defaultAvatar}
              alt={currentUser.name}
              className="ui-avatar ui-avatar--lg"
              onError={(e) => {
                if (e.target.src !== defaultAvatar) {
                  e.target.src = defaultAvatar;
                }
              }}
            />
            <div className="ui-grow ui-prose">
              <h2 className="ui-heading">Welcome, {currentUser.name}!</h2>
              <p className="ui-muted">User ID: {currentUser.user_id || currentUser.id}</p>
            </div>
          </div>
          <div className="ui-cluster">
            {isSuperAdmin && (
              <button onClick={() => window.location.href = '/super-admin'} className="ui-button ui-button--secondary" title="Super Admin Dashboard">
                Admin Panel
              </button>
            )}
            <button onClick={refreshUserData} className="ui-button ui-button--secondary" title="Refresh profile data">
              <svg className="ui-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                <path d="M21 12a9 9 0 1 1-3-6.7" />
                <path d="M21 3v6h-6" />
              </svg>
            </button>
          </div>
        </div>

        <div className="ui-section">
          <h3 className="ui-heading">Your GroupMe Groups</h3>

          {error && (
            <div className="ui-alert ui-alert--error ui-prose" role="alert">
              {error}
            </div>
          )}

          {loading ? (
            <div className="ui-empty" role="status">
              <div className="ui-spinner" aria-hidden="true"></div>
              <p>Loading your groups...</p>
            </div>
          ) : groups.length === 0 ? (
            <div className="ui-empty">
              <p>No groups found. Make sure you're a member of some GroupMe groups, then refresh this page.</p>
            </div>
          ) : (
            <>
            {groups.length >= SEARCH_MIN_GROUPS && (
              <label className="ui-field">
                <span className="ui-muted">Search groups by name</span>
                <input
                  type="search"
                  className="ui-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </label>
            )}
            {visibleGroups.length === 0 ? (
            <div className="ui-empty">
              <p>No groups match "{searchTerm.trim()}".</p>
            </div>
            ) : (
            <div className="ui-grid">
              {visibleGroups.map((group) => (
                <div key={group.id} className="ui-card ui-stack">
                  <div className="ui-cluster ui-cluster--top">
                    <img
                      src={group.image_url || defaultGroup}
                      alt={group.name}
                      className="ui-avatar"
                      onError={(e) => {
                        if (e.target.src !== defaultGroup) {
                          e.target.src = defaultGroup;
                        }
                      }}
                    />
                    <div className="ui-grow ui-prose">
                      <h4 className="ui-heading">{group.name}</h4>
                      <p className="ui-muted">{group.members?.length || 0} members</p>
                      {group.has_yabuddy_bot && (
                        <div className="bot-status">
                          <p>
                            <svg className="ui-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                              <path d="M20 6 9 17l-5-5" />
                            </svg>
                            Bot Active
                          </p>
                          <div
                            className="bot-id clickable"
                            onClick={() => copyToClipboard(group.yabuddy_bot_id)}
                            title="Click to copy Bot ID"
                          >
                            Bot ID: {group.yabuddy_bot_id}
                          </div>
                          {group.ownership_info && (
                            <div className="ownership-info">
                              {group.ownership_info.is_owner ? (
                                <p className="owner-text">You are the owner</p>
                              ) : (
                                <p className="owner-text ui-muted">Owner: {group.ownership_info.owner_user_id}</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {group.has_yabuddy_bot && addedGroupId === group.id && (
                    <div className="ui-alert ui-alert--success ui-stack" role="status">
                      <strong>YaBuddy added</strong>
                      <p className="ui-prose">Bot ID: {group.yabuddy_bot_id}</p>
                      <button
                        onClick={() => copyToClipboard(group.yabuddy_bot_id)}
                        className="ui-button ui-button--secondary"
                      >
                        Copy Bot ID
                      </button>
                      <ContactLine lead="Send this Bot ID to" />
                    </div>
                  )}

                  {group.has_yabuddy_bot ? (
                    confirmingGroupId === group.id ? (
                      <div
                        className="ui-stack"
                        onKeyDown={(e) => {
                          if (e.key === 'Escape' && !processingGroups.has(group.id)) {
                            setConfirmingGroupId(null);
                          }
                        }}
                      >
                        <p>Remove YaBuddy from this group?</p>
                        <div className="ui-cluster">
                          <button
                            ref={confirmButton}
                            onClick={() => confirmRemove(group.yabuddy_bot_id, group.id)}
                            disabled={processingGroups.has(group.id)}
                            className="ui-button ui-button--danger ui-grow"
                          >
                            Confirm remove
                          </button>
                          <button
                            onClick={() => setConfirmingGroupId(null)}
                            disabled={processingGroups.has(group.id)}
                            className="ui-button ui-button--secondary ui-grow"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmingGroupId(group.id)}
                        disabled={processingGroups.has(group.id)}
                        className="ui-button ui-button--outline-danger ui-button--block"
                      >
                        {processingGroups.has(group.id) ? 'Removing...' : 'Remove YaBuddy'}
                      </button>
                    )
                  ) : (
                    <button
                      onClick={() => addBot(group.id)}
                      disabled={processingGroups.has(group.id)}
                      className="ui-button ui-button--primary ui-button--block"
                    >
                      {processingGroups.has(group.id) ? 'Adding...' : 'Add YaBuddy'}
                    </button>
                  )}
                </div>
              ))}
            </div>
            )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default App;
